import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ToastContext = createContext(null);

const MAX_TOASTS = 4;
// An action toast ("Archived. Undo") stays long enough to reach the button.
const ACTION_DURATION = 8000;
let nextId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  // toast: { type, message, traceId?, duration?, action?: { label, onClick } }
  // `action` is optional; a toast without one behaves exactly as before.
  const showToast = useCallback((toast) => {
    nextId += 1;
    const id = `${Date.now()}-${nextId}`;
    setToasts(t => {
      // Deduplicate: skip if the same message is already visible
      if (t.some(x => x.message === toast.message && x.type === toast.type)) return t;
      // Cap the queue — drop the oldest toast if at capacity
      const trimmed = t.length >= MAX_TOASTS ? t.slice(-(MAX_TOASTS - 1)) : t;
      return [...trimmed, { id, ...toast }];
    });

    setTimeout(() => {
      setToasts(t => t.filter(x => x.id !== id));
    }, toast.duration || (toast.action ? ACTION_DURATION : 4000));
    return id;
  }, []);

  const value = useMemo(() => ({ toasts, showToast, dismissToast }), [toasts, showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
